import { unref } from 'vue';
import { cityRepository, licenseRepository } from '@/data';
import { License } from '@/models/License';
import { generateCsvBlob } from '@/csv';

// Shared list behavior; summaries are never handed to an editable detail view.
export function createCitySearchMixin({ itemsProperty, selectedIdProperty, Model, search, selectMethod }) {
  return {
    data: () => ({
      city: null, loaded: false, loadError: null, filtering: false, query: '',
      [itemsProperty]: [], total: 0, pageStart: 0, cursor: null,
      nextCursor: null, previousCursor: null, firstCursor: null, lastCursor: null,
      loadToken: 0, cityLoadToken: 0, openSingleSearchResult: false, searchMetadata: {},
      licenses: [], showLicenses: false, mapLoaded: false, loadingMap: false, mapError: null,
      exporting: false, exportError: null, exportUrl: null
    }),
    computed: {
      searchAuthKey() {
        const user = unref(this.currentUser);
        return JSON.stringify([this.currentUserChecked, this.isAuthenticated, user?.id, user?.role, user?.cities]);
      },
      pageEnd() { return this.pageStart + this[itemsProperty].length; },
      selectedResultIndex() {
        const index = this[itemsProperty].findIndex(item => item.id === this[selectedIdProperty]);
        return index < 0 ? -1 : this.pageStart + index;
      }
    },
    watch: {
      cityId() { this.reloadCity(); },
      searchAuthKey() { this.reloadCity(); },
      showLicenses(value) { if (value) this.reloadLicenses(); },
      async [selectedIdProperty](current, previous) {
        if (current && !this[itemsProperty].some(item => item.id === current)) {
          await this.reloadResults({ selectedId: current });
        } else if (!current && previous) {
          await this.reloadResults();
        }
      }
    },
    mounted() { this.reloadCity(); },
    beforeUnmount() {
      this.loadToken++;
      this.cityLoadToken++;
      this.clearExportUrl();
    },
    methods: {
      clearPage() {
        this[itemsProperty] = [];
        this.total = this.pageStart = 0;
        this.cursor = this.nextCursor = this.previousCursor = this.firstCursor = this.lastCursor = null;
        this.searchMetadata = {};
      },
      async reloadCity() {
        const token = ++this.cityLoadToken;
        this.loadToken++;
        this.loaded = this.filtering = false;
        this.city = null;
        this.loadError = this.exportError = this.mapError = null;
        this.openSingleSearchResult = false;
        this.clearPage();
        this.clearExportUrl();
        this.licenses = [];
        this.mapLoaded = this.loadingMap = false;
        this.onSearchReset?.();
        this.draftFilters = { ...this.filters, sort: { ...this.filters.sort }, ...(this.filters.reasons ? { reasons: { ...this.filters.reasons } } : {}) };
        if (!this.currentUserChecked || !this.isAuthenticated) return;
        try {
          const city = await cityRepository.loadCity(this.cityId, true);
          if (token !== this.cityLoadToken) return;
          this.city = city;
          if (this.showLicenses) this.reloadLicenses();
          await this.reloadResults({ offset: 0, selectedId: this[selectedIdProperty] });
        } catch (error) {
          if (token !== this.cityLoadToken) return;
          this.loadError = error;
          this.loaded = true;
        }
      },
      async reloadResults({ cursor = null, offset = this.pageStart, selectedId = null } = {}) {
        if (!this.city || !this.currentUserChecked || !this.isAuthenticated) return false;
        const token = ++this.loadToken;
        const city = this.city;
        this.filtering = true;
        this.loadError = null;
        try {
          const result = await search(city.id, { ...this.searchOptions(), cursor, offset, selectedId });
          if (token !== this.loadToken) return false;
          this[itemsProperty] = result.items.map(Model.dataConverter(city));
          this.total = result.total;
          this.pageStart = result.offset;
          for (const name of ['cursor', 'nextCursor', 'previousCursor', 'firstCursor', 'lastCursor']) this[name] = result[name];
          const metadata = { ...result };
          delete metadata.items;
          this.searchMetadata = metadata;
          const openSingle = this.openSingleSearchResult;
          this.openSingleSearchResult = false;
          if (openSingle && result.total === 1 && !this[selectedIdProperty]) this[selectMethod](result.items[0].id);
          return true;
        } catch (error) {
          if (token !== this.loadToken) return false;
          this.clearPage();
          this.loadError = error;
          return false;
        } finally {
          if (token === this.loadToken) {
            this.loaded = true;
            this.filtering = false;
          }
        }
      },
      changePage(cursor) {
        if (this.filtering || !cursor) return false;
        return this.reloadResults({ cursor });
      },
      search(query) {
        this.query = query;
        this.openSingleSearchResult = Boolean(query.trim());
        return this.reloadResults({ offset: 0 });
      },
      filterResults() {
        this.openSingleSearchResult = false;
        return this.reloadResults({ offset: 0 });
      },
      async nextResult(backward) {
        if (this.filtering || !this.total) return;
        const id = this[selectedIdProperty];
        const index = this[itemsProperty].findIndex(item => item.id === id);
        let target = index + (backward ? -1 : 1);
        if (index < 0 || target < 0 || target >= this[itemsProperty].length) {
          const cursor = index < 0
            ? (backward ? this.lastCursor : this.firstCursor)
            : (backward ? this.previousCursor || this.lastCursor : this.nextCursor || this.firstCursor);
          if (!await this.reloadResults({ cursor })) return;
          target = backward ? this[itemsProperty].length - 1 : 0;
        }
        if (id === this[selectedIdProperty] && this[itemsProperty][target]) this[selectMethod](this[itemsProperty][target].id);
      },
      async reloadLicenses() {
        if (!this.city || !this.showLicenses || this.loadingMap || this.mapLoaded || !this.isAuthenticated) return;
        const city = this.city;
        const token = this.cityLoadToken;
        this.loadingMap = true;
        this.mapError = null;
        try {
          const { items } = await licenseRepository.mapMarkers(city.id);
          if (token !== this.cityLoadToken) return;
          this.licenses = items.map(License.dataConverter(city));
          this.mapLoaded = true;
        } catch (error) {
          if (token === this.cityLoadToken) this.mapError = error;
        } finally {
          if (token === this.cityLoadToken) this.loadingMap = false;
        }
      },
      clearExportUrl() {
        if (this.exportUrl) URL.revokeObjectURL(this.exportUrl);
        this.exportUrl = null;
      },
      async downloadExport({ fetch, headers, rows, filename }) {
        if (this.exporting || !this.city || !this.isAuthenticated) return;
        const city = this.city;
        const token = this.cityLoadToken;
        this.exporting = true;
        this.exportError = null;
        try {
          const result = await fetch(city.id, this.searchOptions());
          if (token !== this.cityLoadToken || !this.isAuthenticated) return;
          const blob = generateCsvBlob(',', headers, rows(result.items.map(Model.dataConverter(city))));
          this.clearExportUrl();
          this.exportUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = this.exportUrl;
          link.download = `${city.id}_${filename}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (error) {
          if (token === this.cityLoadToken) this.exportError = error;
        } finally { this.exporting = false; }
      }
    }
  };
}
